import { useConfetti } from '@shared/hooks/use-confetti';
import { UserNameDetails } from 'components/UserNameDetails/UserNameDetails';
import { url } from 'constants/urls';
import { useTranslation as useTranslationHook } from 'i18n/useTranslation';
import { Offer } from 'interface/offerInterface';
import { UserDetails } from 'interface/userDetails';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { icons } from '../../constants/assets';

export const SuccessPopup: React.FC<{
  offer: Offer
  orderId: string
  userDetails: UserDetails
  quantity: number
  selectedMembersCount?: number
  onClose: () => void
}> = ({ offer, orderId, userDetails, quantity, selectedMembersCount, onClose }) => {
  const { i18n } = useTranslation();
  const { successPurchaseContent } = useTranslationHook();
  const currentLang = i18n.language;
  const total = offer.price * quantity * (selectedMembersCount || 1);
  const totaQuantity = quantity * (selectedMembersCount || 1);
  const { launchFireworks } = useConfetti();
  const itemQuantitiy = useMemo(() => {
    const { items } = offer.sku;
    const firstKey = Object.keys(items)[0];

    if (!firstKey) {
      return undefined;
    }

    return items[firstKey as keyof Offer['sku']['items']];
  }, [offer]);

  useEffect(() => {
    const interval = launchFireworks();

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex w-full flex-1 items-center justify-center ">
      <div className="z-10 w-full min-w-[300px] max-w-[600px] rounded-xl border-2 border-b-4 border-solid border-black p-4 bg-checkout-card-gradient animate-slide-up-fade-with-glow sm:h-auto sm:px-6">
        <div className="flex flex-row items-center justify-center gap-2 pb-4 font-bold">
          {userDetails && <UserNameDetails username={userDetails.username} />}
          <Link
            className="text-[10px] text-lightBlue underline sm:text-[14px]"
            to={url.redirectToOrderHistory}
            target="_blank"
            rel="noopener noreferrer"
          >
            {orderId}
          </Link>
        </div>

        <div className="mb-16 flex flex-col items-center justify-center rounded-md px-2 py-6 bg-checkout-info-gradient sm:mb-0 sm:px-6 sm:py-4">
          <div className="font-border poppins-2xl pb-4 text-[17.5px] sm:pb-8 sm:text-[30px]">
            {successPurchaseContent.thanksForYourOrder}
          </div>
          <div
            className="relative flex h-[70px] w-full rounded-md sm:h-[110px]"
            style={{
              backgroundImage: `url(${offer.sku.backgroundUrl})`,
              backgroundSize: 'cover',
            }}
          >
            <div className="relative flex items-center justify-center px-4 md:px-7">
              <img
                className={offer.sku.displayOfferType === 'grand' ? 'h-[56px] w-[74px] sm:h-[100px] sm:w-[132px]' : 'size-[56px] sm:size-[100px]'}
                src={offer.sku.imageUrl}
                alt="sku"
              />
              {itemQuantitiy && (
                <div className="font-border poppins-lg absolute right-0 top-2 rounded-lg bg-[#0E122C33] px-2 py-1 text-[15px] sm:text-[17.5px]">
                  {(itemQuantitiy * quantity).toLocaleString()}
                </div>
              )}
            </div>
            <div
              className="
                  my-[-1.5px]
                  mr-[-1.5px]
                  flex
                  flex-1
                  rounded-r-md
                  bg-black p-[1.5px]
                  [clip-path:polygon(4%_0,100%_0%,100%_100%,0%_100%)]
                "
            >
              <div
                className="
                    flex-1
                    rounded-r-md
                    py-[10px]
                    pl-10
                    pr-5
                    [background:linear-gradient(179.98deg,#B149EC_21.24%,#541CAC_155.27%)]
                    [clip-path:polygon(4%_0,100%_0%,100%_100%,0%_100%)]
                  "
              >
                <p className="font-border poppins-md text-[12.5px] sm:text-[17.5px]">
                  {offer.title[currentLang]}
                </p>
                <div className="flex w-fit items-center justify-start gap-2 rounded-md bg-[#0E122C] px-2 py-0.5 sm:py-1">
                  <span className="mt-px text-[12.5px] font-bold sm:text-[22.5px]">
                    {offer.price.toLocaleString()}
                  </span>
                  <img
                    src={icons.powerIcon}
                    alt="power"
                    className="size-3 sm:size-5"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex w-full items-start justify-between rounded-md bg-box_dark_blue px-4 py-2 sm:p-4">
            <div className="flex flex-col items-start sm:gap-2">
              <div className="font-border poppins-2xl text-sm sm:text-[22.5px]">
                {successPurchaseContent.quantity}
              </div>
              <div className="font-border poppins-2xl text-[12.5px] text-lightBlue sm:text-[15px]">
                x
                {totaQuantity}
              </div>
            </div>
            <div className="flex flex-col items-end sm:gap-2">
              <div className="font-border poppins-2xl text-sm sm:text-[22.5px]">
                {successPurchaseContent.total}
              </div>
              <div className="font-border poppins-2xl flex flex-row items-center justify-start gap-1 text-sm sm:text-[22.5px]">
                {total?.toLocaleString()}
                <img
                  src={icons.powerIcon}
                  alt="power"
                  className="size-3 sm:size-5"
                />
              </div>
              {offer.usdcPrice && (
                <div className="font-border poppins-2xl flex flex-row items-center justify-start gap-1 text-[12.5px] text-lightBlue sm:text-[15px]">
                  {(quantity * offer.usdcPrice * (selectedMembersCount || 1)).toFixed(2)}
                  <span>{successPurchaseContent.usdc}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <button
          type="button"
          className="yellow-button font-border poppins-2xl my-4 h-[60px] w-full text-[17.5px] sm:h-[90px] sm:text-[30px]"
          onClick={onClose}
        >
          {successPurchaseContent.continueShopping}
        </button>
      </div>
    </div>
  );
};
